import type { ScanRow } from "./scan-list";

export const REPORT_SELF_SERVE_DAYS = 90;

export const REPORT_DATE_PRESETS = [
  { value: "today", label: "Today", daysBack: 0 },
  { value: "last_7_days", label: "Last 7 days", daysBack: 7 },
  { value: "last_30_days", label: "Last 30 days", daysBack: 30 },
  { value: "last_90_days", label: "Last 90 days", daysBack: 90 },
] as const;

export type ReportDatePreset = (typeof REPORT_DATE_PRESETS)[number]["value"];

export type ReportDateSelection =
  | { readonly preset: ReportDatePreset }
  | {
      readonly preset: "custom";
      readonly fromDay: string;
      readonly toDay: string;
    };

export const DEFAULT_REPORT_DATE_SELECTION = {
  preset: "last_90_days",
} as const satisfies ReportDateSelection;

export interface ReportPickerBounds {
  readonly floorDay: string;
  readonly todayDay: string;
}

export interface ReportResolvedDateRange extends ReportPickerBounds {
  readonly fromIso: string;
  readonly toIso: string;
  readonly fromDay: string;
  readonly toDay: string;
}

export type ReportDateResolution =
  | { readonly kind: "ok"; readonly range: ReportResolvedDateRange }
  | {
      readonly kind: "unavailable";
      readonly reason: "self_serve_floor";
      readonly floorDay: string;
    }
  | { readonly kind: "invalid"; readonly reason: "invalid_range" };

const DAY_MS = 24 * 60 * 60 * 1000;

// Shared local-day boundaries for the separate incident search contract (which
// does not inherit scans' 90-day self-serve floor).
export { localMidnightMs as reportLocalMidnightMs, shiftCalendarDay as shiftReportCalendarDay };
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function dateParts(day: string): { year: number; month: number; day: number } | null {
  const match = DATE_PATTERN.exec(day);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const check = new Date(Date.UTC(year, month - 1, date));
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== date
  ) {
    return null;
  }
  return { year, month, day: date };
}

function dayFromParts(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);
}

function shiftCalendarDay(day: string, amount: number): string {
  const parts = dateParts(day);
  if (parts === null) throw new Error("Invalid report calendar day.");
  return dayFromParts(parts.year, parts.month, parts.day + amount);
}

function zonedParts(instant: Date, timeZone: string): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
} {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(instant);
  const value = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "NaN");
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
}

/** Convert a local midnight to UTC without assuming a fixed zone offset. */
function localMidnightMs(day: string, timeZone: string): number {
  const local = dateParts(day);
  if (local === null) throw new Error("Invalid report calendar day.");
  const desiredAsUtc = Date.UTC(local.year, local.month - 1, local.day, 0, 0, 0, 0);
  let guess = desiredAsUtc;
  for (let pass = 0; pass < 4; pass += 1) {
    const actual = zonedParts(new Date(guess), timeZone);
    const actualAsUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
      0,
    );
    const correction = desiredAsUtc - actualAsUtc;
    guess += correction;
    if (correction === 0) return guess;
  }
  return guess;
}

export function reportCalendarDay(instant: Date, timeZone: string): string {
  const parts = zonedParts(instant, timeZone);
  return dayFromParts(parts.year, parts.month, parts.day);
}

export function reportPickerBounds(
  timeZone: string,
  now: Date = new Date(),
): ReportPickerBounds {
  const floor = new Date(now.getTime() - REPORT_SELF_SERVE_DAYS * DAY_MS);
  return {
    floorDay: reportCalendarDay(floor, timeZone),
    todayDay: reportCalendarDay(now, timeZone),
  };
}

/**
 * Resolve and enforce the Reports self-serve window in one place.
 * Presets use workspace calendar days; the floor itself is an exact 90 * 24h instant.
 */
export function resolveReportDateRange(
  selection: ReportDateSelection,
  timeZone: string,
  now: Date = new Date(),
): ReportDateResolution {
  if (Number.isNaN(now.getTime())) {
    return { kind: "invalid", reason: "invalid_range" };
  }
  let bounds: ReportPickerBounds;
  try {
    bounds = reportPickerBounds(timeZone, now);
  } catch {
    return { kind: "invalid", reason: "invalid_range" };
  }
  const { floorDay, todayDay } = bounds;
  const floorMs = now.getTime() - REPORT_SELF_SERVE_DAYS * DAY_MS;

  let requestedFromDay: string;
  let requestedToDay: string;
  if (selection.preset === "custom") {
    if (dateParts(selection.fromDay) === null || dateParts(selection.toDay) === null) {
      return { kind: "invalid", reason: "invalid_range" };
    }
    requestedFromDay = selection.fromDay;
    requestedToDay = selection.toDay;
    if (requestedFromDay > todayDay || requestedToDay > todayDay) {
      return { kind: "invalid", reason: "invalid_range" };
    }
  } else {
    const preset = REPORT_DATE_PRESETS.find((item) => item.value === selection.preset);
    if (preset === undefined) return { kind: "invalid", reason: "invalid_range" };
    requestedFromDay = shiftCalendarDay(todayDay, -preset.daysBack);
    requestedToDay = todayDay;
  }

  if (requestedFromDay > requestedToDay) {
    return { kind: "invalid", reason: "invalid_range" };
  }

  let requestedFromMs: number;
  let requestedToMs: number;
  try {
    requestedFromMs = localMidnightMs(requestedFromDay, timeZone);
    // Resolve the following midnight separately so DST-short and DST-long days stay correct.
    requestedToMs = localMidnightMs(shiftCalendarDay(requestedToDay, 1), timeZone) - 1;
  } catch {
    return { kind: "invalid", reason: "invalid_range" };
  }
  if (requestedToMs < floorMs) {
    return { kind: "unavailable", reason: "self_serve_floor", floorDay };
  }
  if (requestedFromMs > now.getTime()) {
    return { kind: "invalid", reason: "invalid_range" };
  }

  const fromMs = Math.max(requestedFromMs, floorMs);
  const toMs = Math.min(requestedToMs, now.getTime());
  if (fromMs > toMs) {
    return { kind: "invalid", reason: "invalid_range" };
  }

  return {
    kind: "ok",
    range: {
      floorDay,
      todayDay,
      fromDay: reportCalendarDay(new Date(fromMs), timeZone),
      toDay: reportCalendarDay(new Date(toMs), timeZone),
      fromIso: new Date(fromMs).toISOString(),
      toIso:
        toMs === requestedToMs
          ? new Date(toMs).toISOString().replace(/\.999Z$/, ".999999Z")
          : new Date(toMs).toISOString(),
    },
  };
}

export function isReportCustomRangeSelectable(
  fromDay: string,
  toDay: string,
  bounds: ReportPickerBounds,
): boolean {
  return (
    dateParts(fromDay) !== null &&
    dateParts(toDay) !== null &&
    fromDay >= bounds.floorDay &&
    toDay <= bounds.todayDay &&
    fromDay <= toDay
  );
}

export function reportDateSelectionKey(selection: ReportDateSelection): string {
  return selection.preset === "custom"
    ? `custom:${selection.fromDay}:${selection.toDay}`
    : selection.preset;
}

export function reportDateLabel(selection: ReportDateSelection): string {
  if (selection.preset !== "custom") {
    return REPORT_DATE_PRESETS.find((item) => item.value === selection.preset)?.label ?? "Dates";
  }
  return `${selection.fromDay} to ${selection.toDay}`;
}

export function reportDateMetadataLabel(
  selection: ReportDateSelection,
  range: ReportResolvedDateRange,
): string {
  const label = selection.preset === "custom" ? "Custom" : reportDateLabel(selection);
  return `${label} (${range.fromDay} to ${range.toDay})`;
}

export const REPORT_SITE_SELECTION_MAX = 12;
export const REPORT_SAVED_VIEW_MAX = 20;

export const REPORT_SCAN_METHODS = ["QR", "NFC", "MANUAL"] as const;
export type ReportScanMethod = (typeof REPORT_SCAN_METHODS)[number];

export const REPORT_LIFECYCLE_STATES = [
  "captured",
  "queued",
  "synced",
  "validated",
  "flagged",
  "rejected",
] as const;
export type ReportScanLifecycleState = (typeof REPORT_LIFECYCLE_STATES)[number];

export const REPORT_COLUMN_KEYS = [
  "captured",
  "received",
  "checkpoint",
  "site",
  "route",
  "guard",
  "method",
  "status",
  "gps",
  "validation_notes",
] as const;
export type ReportColumnKey = (typeof REPORT_COLUMN_KEYS)[number];

export const DEFAULT_REPORT_COLUMNS: readonly ReportColumnKey[] = [
  "captured",
  "checkpoint",
  "site",
  "route",
  "guard",
  "method",
  "status",
  "gps",
] as const;

export interface ReportScanSort {
  readonly column: ReportColumnKey;
  readonly direction: "asc" | "desc";
}

export const DEFAULT_REPORT_SORT: ReportScanSort = {
  column: "captured",
  direction: "desc",
};

export interface ReportScanFilterSets {
  readonly siteNames: readonly string[];
  readonly guardName: string | null;
  readonly routeName: string | null;
  readonly checkpointName: string | null;
  readonly methods: readonly ReportScanMethod[];
  readonly presenceAdhocOnly: boolean;
  readonly lifecycleStates: readonly ReportScanLifecycleState[];
  readonly reviewRequiredOnly: boolean;
  readonly dateSelection: ReportDateSelection;
  readonly columns: readonly ReportColumnKey[];
  readonly sort: ReportScanSort;
}

export const DEFAULT_REPORT_FILTER_SETS: ReportScanFilterSets = {
  siteNames: [],
  guardName: null,
  routeName: null,
  checkpointName: null,
  methods: [],
  presenceAdhocOnly: false,
  lifecycleStates: [],
  reviewRequiredOnly: false,
  dateSelection: DEFAULT_REPORT_DATE_SELECTION,
  columns: DEFAULT_REPORT_COLUMNS,
  sort: DEFAULT_REPORT_SORT,
};

export const REPORT_METHOD_LABELS: Record<ReportScanMethod, string> = {
  QR: "QR",
  NFC: "NFC",
  MANUAL: "Manual",
};

export const REPORT_LIFECYCLE_LABELS: Record<ReportScanLifecycleState, string> = {
  captured: "Captured",
  queued: "Queued",
  synced: "Synced",
  validated: "Validated",
  flagged: "Flagged",
  rejected: "Rejected",
};

export const REPORT_COLUMN_LABELS: Record<ReportColumnKey, string> = {
  captured: "Captured (device)",
  received: "Received (server)",
  checkpoint: "Checkpoint",
  site: "Site",
  route: "Route",
  guard: "Guard",
  method: "Method",
  status: "Status",
  gps: "GPS quality",
  validation_notes: "Validation notes",
};

export const REPORT_SORTING_WITHIN_WINDOW_CLAUSE = "Sorting applies within this window.";

export const REPORT_TOO_MANY_SITES_COPY =
  "You can select up to 12 sites. Remove a site before adding another.";

export const REPORT_SUPPORT_CSV_UNAVAILABLE_COPY =
  "Reports is unavailable right now. Check your access or try again.";

export const REPORT_CHECKPOINT_WINDOW_HINT =
  "Checkpoint options come from this result window for the chosen route, not a complete catalogue.";

/** Unique non-empty labels for Reports filter pickers, sorted for stable UI. */
export function uniqueScanLabels(
  rows: readonly ScanRow[],
  key: "siteName" | "guardName" | "routeName" | "checkpointName",
): string[] {
  const labels = new Set<string>();
  for (const row of rows) {
    const value = row[key];
    if (typeof value === "string" && value.trim().length > 0) {
      labels.add(value.trim());
    }
  }
  return [...labels].sort((left, right) => left.localeCompare(right));
}

export function mergeScanLabels(
  existing: readonly string[],
  incoming: readonly string[],
): string[] {
  return [...new Set([...existing, ...incoming])].sort((left, right) =>
    left.localeCompare(right),
  );
}

/**
 * Toggle a site label in the selection. Selecting an already-chosen site removes it.
 * Cap at 12 unique labels; refuse a 13th add without mutating the prior selection.
 */
export function toggleReportSite(
  current: readonly string[],
  nextLabel: string,
):
  | { readonly ok: true; readonly siteNames: readonly string[]; readonly action: "added" | "removed" | "noop" }
  | { readonly ok: false; readonly reason: "too_many_sites"; readonly max: number } {
  const label = nextLabel.trim();
  if (label.length === 0) {
    return { ok: true, siteNames: current, action: "noop" };
  }
  if (current.includes(label)) {
    return {
      ok: true,
      siteNames: current.filter((site) => site !== label),
      action: "removed",
    };
  }
  if (current.length >= REPORT_SITE_SELECTION_MAX) {
    return { ok: false, reason: "too_many_sites", max: REPORT_SITE_SELECTION_MAX };
  }
  return { ok: true, siteNames: [...current, label], action: "added" };
}

export function reportSitesChipValue(siteNames: readonly string[]): string {
  if (siteNames.length === 0) return "";
  if (siteNames.length === 1) return siteNames[0] ?? "";
  if (siteNames.length === 2) return `${siteNames[0]}, ${siteNames[1]}`;
  return `${siteNames[0]}, ${siteNames[1]}, +${siteNames.length - 2}`;
}

export function reportFilterChipText(
  label:
    | "Site"
    | "Sites"
    | "Guard"
    | "Route"
    | "Checkpoint"
    | "Dates"
    | "Method"
    | "Presence"
    | "Status"
    | "Review required",
  value: string,
): string {
  return `${label} · ${value}`;
}

export function isReportScanMethod(value: string): value is ReportScanMethod {
  return (REPORT_SCAN_METHODS as readonly string[]).includes(value);
}

export function isReportLifecycleState(value: string): value is ReportScanLifecycleState {
  return (REPORT_LIFECYCLE_STATES as readonly string[]).includes(value);
}

export function isReportColumnKey(value: string): value is ReportColumnKey {
  return (REPORT_COLUMN_KEYS as readonly string[]).includes(value);
}

export const REPORT_WINDOW_HINT =
  "Up to the most recent 500 matching scans. Older scans stay stored; this window does not delete them.";

export const REPORT_UNAVAILABLE_COPY =
  "Scans older than 90 days are not shown here. SecOps keeps them for at least 12 months. Ask SecOps support to retrieve them.";

export const REPORT_PICKER_FLOOR_COPY = REPORT_UNAVAILABLE_COPY;

/** Honest operator copy for the Reports 500-row window (not a deletion claim). */
export function reportWindowCopy(input: {
  shownCount: number;
  limit: number;
  siteFilter: string;
  guardFilter: string;
  dateSelection: ReportDateSelection;
  cappedSorted?: boolean;
}): { foot: string; pageHint: string } {
  const scope: string[] = [];
  if (input.siteFilter.length > 0) scope.push(`site ${input.siteFilter}`);
  if (input.guardFilter.length > 0) scope.push(`guard ${input.guardFilter}`);
  const scopeClause = scope.length > 0 ? ` matching ${scope.join(" and ")}` : "";
  const datesSentence = `Dates: ${reportDateLabel(input.dateSelection)}.`;
  const sortClause =
    input.cappedSorted === true ? ` ${REPORT_SORTING_WITHIN_WINDOW_CLAUSE}` : "";

  if (input.shownCount === 0) {
    return {
      pageHint: REPORT_WINDOW_HINT,
      foot: "No scans match those filters in this window.",
    };
  }
  if (input.shownCount >= input.limit) {
    return {
      pageHint: REPORT_WINDOW_HINT,
      foot: `Showing the most recent ${input.limit} scans${scopeClause}. ${datesSentence} Older matching scans stay stored and are outside this view and CSV. Narrow the filters or the date range to reach them.${sortClause}`,
    };
  }
  const scansLabel = input.shownCount === 1 ? "1 scan" : `${input.shownCount} scans`;
  return {
    pageHint: REPORT_WINDOW_HINT,
    foot: `Showing all ${scansLabel}${scopeClause}. ${datesSentence} The CSV includes every row shown.`,
  };
}
